import { FoundingOfferStrip } from "./shared/FoundingOffer";
import React, { useEffect, useState } from "react";

// Reuses the app's own logo mark (see resources/icon.svg) so the site and
// the packaged app look like the same product, not two different brands.
function LogoMark({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <path d="M28 30 H62 M45 30 V70" fill="none" stroke="#F1ECFB" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M45 46 H58 A9 9 0 0 1 58 64 H45" fill="none" stroke="#F1ECFB" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="72" cy="70" r="6" fill="#E1672E" />
    </svg>
  );
}

const OWNER_FEATURES = [
  "Track rent — received vs pending, at a glance",
  "Onboard tenants with Aadhaar-based ID capture",
  "Manage PG properties bed by bed",
  "List vacant units on the built-in TO-LET marketplace",
  "Send WhatsApp rent receipts and direct UPI pay links",
  "Handle maintenance requests with photos",
];

const TENANT_FEATURES = [
  "See your tenancy, rent history, and due dates in one place",
  "Pay rent directly and keep a running payment history",
  "Raise maintenance requests with photos and track status",
  "Browse residential, PG, and commercial listings nearby",
];

const PROPERTY_TYPES = [
  { num: "01", name: "Residential", desc: "Flats and independent houses, with full tenant and rent tracking." },
  { num: "02", name: "Commercial", desc: "Shops and offices — carpet area, GST, parking, and lease terms." },
  { num: "03", name: "PG / Hostel", desc: "Manage occupancy bed by bed, with per-bed TO-LET listing." },
];

const PLAY_LINK = "https://play.google.com/store/apps/details?id=in.toletbook.app";
// The festive bar and badge row show until the day after the Dussehra week, then disappear on their own.
// Admin > Offers > Festive banner controls these (served by GET /api/campaign). Until the answer
// arrives, or if it fails, fall back to the Dussehra defaults so the page never looks empty.
interface Campaign { festive: boolean; festiveText: string; playButton: boolean }
const DEFAULT_CAMPAIGN: Campaign = {
  festive: Date.now() < new Date("2026-10-27T00:00:00+05:30").getTime(),
  festiveText: "Happy Dussehra! Gift for owners: the first 100 get Premium FREE for 3 months, the next 400 get Pro free",
  playButton: true,
};

function PlayBadge({ where, className }: { where: string; className?: string }) {
  return (
    <a
      className={`lp-play ${className ?? ""}`}
      href={PLAY_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-attr={`landing-play-${where}`}
      aria-label="Get ToLetBook on Google Play"
    >
      <img src="/dussehra/google-play-badge.png" alt="Get it on Google Play" width="646" height="250" />
    </a>
  );
}

export function LandingPage({ onGetStarted }: { onGetStarted: () => void }) {
  const [campaign, setCampaign] = useState<Campaign>(DEFAULT_CAMPAIGN);
  useEffect(() => {
    fetch("/api/campaign")
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => c && setCampaign(c))
      .catch(() => {});
  }, []);
  const festive = campaign.festive;
  useEffect(() => {
    document.body.classList.add("landing-active");
    return () => document.body.classList.remove("landing-active");
  }, []);

  return (
    <div className="landing">
      {festive && (
        <a className="lp-fest" href={PLAY_LINK} target="_blank" rel="noopener noreferrer" data-attr="landing-fest-bar">
          <span className="lp-fest-text">{campaign.festiveText}</span>
          <span className="lp-fest-go">Get the app →</span>
        </a>
      )}
      <nav className="lp-nav">
        <div className="lp-nav-brand">
          <span className="lp-nav-mark">
            <LogoMark />
          </span>
          ToLetBook
        </div>
        <div className="lp-nav-right">
          {campaign.playButton && <PlayBadge where="nav" className="lp-play-nav" />}
          <button className="lp-nav-cta" onClick={onGetStarted}>
            Log in
          </button>
        </div>
      </nav>

      <header className="lp-hero wrap">
        <div className="lp-hero-grid">
          <div>
            <span className="lp-hero-badge">Built for Indian rentals</span>
            <h1>
              Every rental,
              <br />
              <span className="accent">written down</span> properly.
            </h1>
            <p className="lp-hero-sub">
              One register for owners and tenants — residential, commercial, and PG/hostel — to track rent, onboard
              tenants, and manage maintenance without a spreadsheet or a notebook that goes missing.
            </p>
            {festive && (
              <div className="lp-dl">
                {campaign.playButton && <PlayBadge where="hero" className="lp-play-hero" />}
                <div className="lp-dl-note">
                  <b>Free on Android</b>
                  <span>Dussehra gift: Premium free for 3 months for the first 100 owners, then Pro free for the next 400. No card needed.</span>
                </div>
              </div>
            )}
            <FoundingOfferStrip />
            <div className="lp-hero-ctas">
              <button className="lp-btn-primary" onClick={onGetStarted}>
                Get started free
              </button>
              <button className="lp-btn-ghost" onClick={onGetStarted}>
                I already have an account
              </button>
            </div>
            <div className="lp-hero-chips">
              <span className="lp-chip">Residential</span>
              <span className="lp-chip">Commercial</span>
              <span className="lp-chip">PG / Hostel</span>
            </div>
          </div>

          <div className="lp-ledger" aria-hidden="true">
            <div className="lp-stamp">
              Verified
              <br />
              Tenant
            </div>
            <div className="lp-ledger-card">
              <div className="lp-ledger-head">
                <span className="lbl">Received this month</span>
                <span className="val">₹16,500</span>
              </div>
              <div className="lp-ledger-row">
                <div className="who">2BHK · Flat 302</div>
                <div className="where">Rajesh Kumar · Paid 3 Sept</div>
              </div>
              <div className="lp-ledger-row">
                <div className="who">Sri Sai Men's PG</div>
                <div className="where">Governorpet · 2/3 beds filled</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="lp-section wrap">
        <div className="lp-section-head">
          <span className="lp-eyebrow">Every property type</span>
          <h2>One app, three kinds of rentals</h2>
        </div>
        <div className="lp-types-grid">
          {PROPERTY_TYPES.map((t) => (
            <div className="lp-type-card" key={t.name}>
              <div className="num">{t.num}</div>
              <h3>{t.name}</h3>
              <p>{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-section wrap">
        <div className="lp-section-head">
          <span className="lp-eyebrow">Built for both sides</span>
          <h2>For owners and tenants alike</h2>
        </div>
        <div className="lp-audience-grid">
          <div className="lp-audience-card emphasis">
            <span className="tag">Owners</span>
            <h3>Run your properties, not spreadsheets</h3>
            <ul className="lp-feature-list">
              {OWNER_FEATURES.map((f) => (
                <li key={f}>
                  <span className="ico">✓</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="lp-audience-card">
            <span className="tag">Tenants</span>
            <h3>Everything about your rental, in one place</h3>
            <ul className="lp-feature-list">
              {TENANT_FEATURES.map((f) => (
                <li key={f}>
                  <span className="ico">✓</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="wrap">
        <div className="lp-trust">
          <div className="lp-trust-item">
            <span className="dot" />
            Payments via Razorpay
          </div>
          <div className="lp-trust-item">
            <span className="dot" />
            Optional DigiLocker identity verification
          </div>
          <div className="lp-trust-item">
            <span className="dot" />
            Aadhaar data encrypted at rest
          </div>
        </div>
      </section>

      <section className="lp-section wrap">
        <div className="lp-app-cta">
          <h2>Get started in minutes</h2>
          <p>Use ToLetBook right now on the web, or install the Android app from Google Play.</p>
          <div className="lp-app-ctas">
            <button className="lp-btn-primary" onClick={onGetStarted}>
              Get started free
            </button>
            <a className="lp-store-badge" href="https://play.google.com/store/apps/details?id=in.toletbook.app" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              ▶ Get it on Google Play
            </a>
          </div>
        </div>
      </section>

      <footer className="lp-footer wrap">
        <div className="lp-footer-row">
          <div className="lp-footer-copy">© {new Date().getFullYear()} ToLetBook</div>
          <div className="lp-footer-links">
            <a href="/privacy.html">Privacy Policy</a>
            <a href="/terms.html">Terms</a>
            <a href="/delete-account.html">Delete account</a>
            <a href="mailto:support@toletbook.in">support@toletbook.in</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
